import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigationGuard } from "@/hooks/useNavigationGuard";
import { TriangleAlert, X } from "lucide-react";
import { Notify, toast } from "@/components/ui/notify";
import { periodReadyToSimulate } from "@/types/sim-sandbox.types";
import type { SimulationObjectiveChoice } from "@/types/sim-sandbox.types";
import { objectiveMissingLabel } from "@/types/sim-sandbox.types";
import { objectiveFromLastRun } from "@/types/sim-objective-restore";
import { useSandboxDraft } from "@/features/simulations/sandbox/hooks/useSandboxDraft";
import { useSandboxController } from "@/features/simulations/sandbox/hooks/useSandboxController";
import { useSandboxPreview } from "@/features/simulations/sandbox/hooks/useSandboxPreview";
import { isSimulationRunning, useSimulate } from "@/features/simulations/sandbox/hooks/useSimulate";
import { SandboxToolbar } from "@/features/simulations/sandbox/SandboxToolbar";
import { PeriodDrawer } from "@/features/simulations/sandbox/PeriodDrawer";
import { ScenarioCalendarDrawer } from "@/features/simulations/sandbox/calendar";
import { SandboxCanvas } from "@/features/simulations/sandbox/SandboxCanvas";
import { MachineParamsSheet } from "@/features/simulations/sandbox/MachineParamsSheet";
import { ObjectiveHeadline } from "@/features/simulations/headline";
import { useAutoBatch } from "@/features/simulations/hooks";

// Sandbox no padrão do protótipo: toolbar (nome/período/produto +
// Sugerir/Salvar/Rodar) sobre um grid [resultado | canvas]. A rodada detalhada é disparada na
// toolbar; a coluna de resultado só exibe. Nada escreve na linha real.
export function SimulationSandbox() {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const {
    scenario,
    ready,
    lineDrift,
    hasBaseLine: hasLine,
    saveStructure,
    restore,
    isSaving,
    isError: scenarioIsError,
    error: scenarioError,
    refetchScenario,
  } = useSandboxController(scenarioId);
  const [driftDismissed, setDriftDismissed] = useState(false);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [objective, setObjective] = useState<SimulationObjectiveChoice | null>(null);
  // Rascunho sujo NO MOMENTO da resposta não conta: o que importa é ter mexido DEPOIS dela.
  const [answeredBatchId, setAnsweredBatchId] = useState<string | null>(null);

  const draft = useSandboxDraft();

  // Registra a guarda de trabalho não salvo: a navegação (assistente ou manual)
  // confirma o descarte quando o rascunho está sujo. Limpa ao sair da tela.
  const setGuard = useNavigationGuard((s) => s.setGuard);
  const dirty = draft.dirty;
  useEffect(() => {
    setGuard(() => dirty, "O rascunho do sandbox tem alterações não salvas. Descartar e sair?");
    return () => setGuard(null);
  }, [dirty, setGuard]);

  const machines = useMemo(() => Object.values(draft.machines), [draft.machines]);
  const selected = draft.selectedKey ? draft.machines[draft.selectedKey] : null;

  const periodReady = periodReadyToSimulate(draft.period);
  const isOp = draft.period.mode !== "FREE";
  const hasPresent = machines.some((m) => m.present);
  const canRun = !!draft.productId && hasPresent && periodReady;

  const preview = useSandboxPreview(scenarioId, !!scenario?.structureSnap);
  // Reabrir um cenário salvo devolve a PERGUNTA e a RESPOSTA da última
  // execução. Sem isto a tela voltava em branco e o usuário pagava um lote inteiro para rever algo
  // já calculado.
  // A reposição é DERIVADA, não sincronizada por efeito: guardar a escolha restaurada em estado
  // exigiria um setState dentro de effect (renderização em cascata) e criaria uma segunda fonte da
  // mesma verdade. A escolha do usuário tem precedência; a execução recém-disparada também.
  const lastRun = scenario?.lastRun ?? null;
  const restoredObjective = useMemo(() => objectiveFromLastRun(lastRun), [lastRun]);
  const effectiveObjective = objective ?? restoredObjective;

  const {
    simulate,
    batchId: freshBatchId,
    isPending: simulatePending,
  } = useSimulate({ scenarioId, objective: effectiveObjective, saveStructure });
  const batchId = freshBatchId ?? lastRun?.id ?? null;
  // O lote exibido pode ser a resposta restaurada ou a que acabou de sair: o botão só volta a
  // travar quando existe rodada EM VOO (ver `isSimulationRunning`). Mesma queryKey da faixa de
  // resposta — uma requisição, dois leitores.
  const batchQuery = useAutoBatch(batchId ?? "", batchId ? 4_000 : undefined);

  const blockers = useMemo(() => {
    const list: string[] = [];
    if (!draft.productId) list.push("Selecione o produto.");
    if (!hasPresent) list.push("Ative ao menos uma máquina no cenário.");
    if (!periodReady) list.push("Escolha a Ordem de Produção do período.");
    // Simular sem saber o que se quer responder deixou de fazer sentido.
    const missingObjective = objectiveMissingLabel(effectiveObjective);
    if (missingObjective) list.push(missingObjective);
    return list;
  }, [draft.productId, hasPresent, periodReady, effectiveObjective]);

  // A rodada avulsa deu lugar ao lote (configuração atual + variações num clique):
  // não há mais execução isolada a acompanhar, só o lote.
  async function onSave() {
    try {
      await saveStructure();
      toast.success("Cenário salvo.");
    } catch {
      /* saveStructure já emite o toast de erro */
    }
  }

  if (!scenarioId)
    return <p className="p-6 text-sm text-muted-foreground">Cenário não informado.</p>;

  if (scenarioIsError) {
    return (
      <div className="p-6">
        <Notify.QueryError error={scenarioError} onRetry={() => void refetchScenario()} />
      </div>
    );
  }

  if (!ready) return <p className="p-6 text-sm text-muted-foreground">Carregando cenário…</p>;

  return (
    <section className="space-y-4">
      <SandboxToolbar
        scenarioName={scenario?.name ?? "Sandbox de simulação"}
        period={draft.period}
        productId={draft.productId}
        onProduct={draft.setProduct}
        productReadOnly={isOp}
        onOpenPeriod={() => setPeriodOpen(true)}
        onOpenCalendar={() => setCalendarOpen(true)}
        runningHours={preview.runningHours}
        effectiveHours={preview.effectiveHours}
        calendarBasis={preview.calendarBasis}
        onSave={() => void onSave()}
        onSimulate={() => void simulate()}
        objective={effectiveObjective}
        onObjective={setObjective}
        baseLineId={draft.baseLineId}
        canRun={canRun && blockers.length === 0}
        isRunning={isSimulationRunning({
          pending: simulatePending,
          batchId,
          status: batchQuery.data?.status,
          isError: batchQuery.isError,
        })}
        isSaving={isSaving}
      />

      {lineDrift && !driftDismissed ? (
        <div className="flex items-center gap-2 rounded-md border border-amber-400/50 bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <TriangleAlert className="h-4 w-4 shrink-0" />
          <span className="flex-1">
            A linha base mudou desde que este cenário foi criado — as premissas podem estar
            desatualizadas.
          </span>
          <button
            type="button"
            aria-label="Dispensar aviso"
            className="rounded p-0.5 hover:bg-amber-400/20"
            onClick={() => setDriftDismissed(true)}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}

      {blockers.length > 0 ? (
        <ul className="space-y-1 rounded-md border border-amber-400/50 bg-amber-50 p-3 text-[13px] text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          {blockers.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      ) : null}

      {/* A RESPOSTA em largura total, acima do canvas: é o objeto primário da tela. */}
      <ObjectiveHeadline
        batchId={batchId}
        objective={effectiveObjective}
        dirty={draft.dirty && answeredBatchId === batchId}
        backtestOrderId={
          draft.period.mode === "OP_EXECUTED" ? draft.period.sourceProductionOrderId : null
        }
        onDone={() => setAnsweredBatchId(batchId)}
      />

      <div className="space-y-4">
        <SandboxCanvas
          bottleneckStageKey={preview.bottleneckStageKey}
          perStage={preview.perStage}
          onRestore={restore}
          restoreLabel={hasLine ? "Restaurar linha original" : "Limpar arranjo"}
        />
        {selected ? (
          <MachineParamsSheet
            machine={selected}
            stages={draft.stages}
            previewError={preview.serverError}
            onRetryPreview={preview.retryServer}
          />
        ) : null}
      </div>

      <PeriodDrawer
        open={periodOpen}
        onOpenChange={setPeriodOpen}
        period={draft.period}
        onChange={draft.setPeriod}
        lineId={draft.baseLineId}
        onProduct={draft.setProduct}
        runningHours={preview.runningHours}
        effectiveHours={preview.effectiveHours}
      />
      <ScenarioCalendarDrawer
        open={calendarOpen}
        onOpenChange={setCalendarOpen}
        scenarioId={scenarioId ?? ""}
        lineId={draft.baseLineId}
        initial={scenario?.calendarOverride ?? null}
      />
    </section>
  );
}
